import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/components/ui/use-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { useAuthContext } from '@/contexts/AuthContext';
import { formatCurrency, formatDocument } from '@/lib/utils';
import {
  getBusinessOpportunity,
  getHubspotOpportunityById,
  getHubspotLineItemsByDealId,
  mapHubspotStageToStatus,
  getHubspotQuotesByDealId
} from '@/services/business-opportunities';
import {
  BusinessOpportunity,
  HubspotOpportunity,
  HubspotLineItem,
  OpportunityStatus
} from '@/types/business-opportunities';
import { 
  ArrowLeft, 
  Building,
  Calendar,
  ChevronLeft,
  Copy,
  Edit, 
  FileText, 
  MoreHorizontal, 
  Package, 
  Pencil,
  Plus, 
  RefreshCw, 
  Trash2,
  User,
  DollarSign,
  ExternalLink,
  Link,
  Users,
  Percent,
  CalendarRange,
  BarChart4,
  TrendingUp
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { supabase } from '@/lib/supabase';
import { initializeHubspotTables, checkHubspotOpportunityExists } from '@/utils/initialize-hubspot-data';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { format } from 'date-fns';
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { getBusinessOpportunityById } from "@/lib/services/business-opportunities";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

type OpportunityWithDetails = BusinessOpportunity & {
  customer: {
    id: string;
    name: string;
    logo_url?: string;
    business_identification_number?: string;
    legal_name?: string;
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
};

// Extended type for Hubspot opportunities with line items
type HubspotOpportunityWithDetails = HubspotOpportunity & {
  customer?: {
    id: string;
    name: string;
    logo_url?: string;
    business_identification_number?: string;
    legal_name?: string;
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
    // Additional fields from hubspotcompanies view
    industry?: string;
    phone?: string;
    ownername?: string;
    owneremail?: string;
    annualrevenue?: number;
    domain?: string;
    alunos?: number;
    classificacao?: string;
    razao_social?: string;
    faixa_alunos?: string;
    hubspotscore?: number;
    lifecyclestage?: string;
    customer_address?: string; // Added customer_address field
  };
  line_items?: HubspotLineItem[];
  quotes?: Array<{
    id: string;
    hs_quote_number?: string;
    hs_status?: string;
    hs_quote_amount?: number;
    hs_currency?: string;
    hs_createdate?: string;
    hs_expiration_date?: string;
    hs_quote_link?: string;
    hs_pdf_download_link?: string;
    deal_id?: string;
    hs_terms?: string;
    hs_sender_firstname?: string;
    hs_sender_lastname?: string;
    hs_sender_email?: string;
  }>;
  owner_name?: string; // Owner name at the opportunity level
  label?: string; // Added label field for deal stage
};

const statusLabels = {
  open: 'Aberta',
  negotiation: 'Em Negociação',
  won: 'Ganha',
  lost: 'Perdida',
  canceled: 'Cancelada'
};

const statusColors = {
  open: 'bg-blue-50 text-blue-700 border-blue-200',
  negotiation: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  won: 'bg-green-50 text-green-700 border-green-200',
  lost: 'bg-red-50 text-red-700 border-red-200',
  canceled: 'bg-gray-50 text-gray-700 border-gray-200'
};

const BusinessOpportunityDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthContext();
  
  const [opportunity, setOpportunity] = useState<OpportunityWithDetails | null>(null);
  const [hubspotOpportunity, setHubspotOpportunity] = useState<HubspotOpportunityWithDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [errorDetails, setErrorDetails] = useState<{message: string, technical?: string}>({ message: '' });
  const [isHubspotOpportunity, setIsHubspotOpportunity] = useState(false);
  const [showHubspotData, setShowHubspotData] = useState(true);
  // Add state for tracking failed image loads
  const [failedImages, setFailedImages] = useState<Record<string, boolean>>({});

  // Helper function for Hubspot stage labels
  const getHubspotStageLabel = (dealstage: string): string => {
    // Convert dealstage IDs to readable labels
    if (dealstage.includes('closedwon')) return 'Ganha';
    if (dealstage.includes('closedlost')) return 'Perdida';
    if (dealstage.includes('qualifiedtobuy')) return 'Qualificada';
    if (dealstage.includes('presentation')) return 'Apresentação';
    if (dealstage.includes('decisionmaker')) return 'Tomador de Decisão';
    if (dealstage.includes('proposal')) return 'Proposta';
    if (dealstage.includes('negotiation')) return 'Negociação';
    if (dealstage.includes('appointment')) return 'Reunião Agendada';
    if (dealstage.includes('discovery')) return 'Descoberta';
    if (dealstage.includes('demo')) return 'Demonstração';
    return dealstage; // Return the original value if no match
  };

  // Error display component
  const ErrorDisplay = () => (
    <div className="container mx-auto py-10">
      <div className="max-w-lg mx-auto bg-white rounded-lg shadow-sm p-6 text-center">
        <div className="flex items-center justify-center h-16 w-16 rounded-full bg-red-50 mx-auto mb-4">
          <div className="h-10 w-10 text-red-500">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
          </div>
        </div>
        <h2 className="text-2xl font-extrabold text-gray-900 mb-2">Erro ao carregar a oportunidade</h2>
        <p className="text-gray-600 mb-6">{errorDetails.message}</p>
        {errorDetails.technical && (
          <div className="bg-gray-50 p-3 rounded text-left mb-6">
            <p className="text-sm text-gray-500 mb-1">Detalhes técnicos:</p>
            <code className="text-xs text-gray-700 block overflow-x-auto">
              {errorDetails.technical}
            </code>
          </div>
        )}
        <Button 
          onClick={() => navigate('/business-opportunities')} 
          variant="outline"
          className="mr-3"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar para lista
        </Button>
        <Button onClick={() => window.location.reload()}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Tentar novamente
        </Button>
      </div>
    </div>
  );

  useEffect(() => {
    const fetchOpportunityData = async () => {
      if (!id) {
        setErrorDetails({
          message: 'ID da oportunidade não fornecido',
          technical: 'Nenhum ID foi fornecido na URL'
        });
        setIsError(true);
        setIsLoading(false);
        return;
      }
      
      try {
        setIsLoading(true);
        
        // Initialize Hubspot tables if they don't exist
        await initializeHubspotTables();
        
        // Check if this is a Hubspot opportunity first
        const isHubspotOpp = await checkHubspotOpportunityExists(id);
        
        if (isHubspotOpp) {
          // Fetch the Hubspot opportunity with its related customer
          const hubspotOpp = await getHubspotOpportunityById(id);
          
          if (!hubspotOpp) {
            throw new Error(`Opportunity with ID ${id} not found in Hubspot`);
          }
          
          // Fetch line items related to this deal
          const lineItems = await getHubspotLineItemsByDealId(id);
          
          // Fetch quotes related to this deal
          const quotes = await getHubspotQuotesByDealId(id);
          
          // Create combined data structure with type casting to match expected types
          const opportunityData: HubspotOpportunityWithDetails = {
            ...hubspotOpp,
            customer_name: hubspotOpp.customer?.name || '',
            line_items: lineItems.map(item => ({
              id: item.id,
              deal_id: item.deal_id,
              name: item.name,
              quantity: item.quantity || 0, // Ensure required properties have defaults
              price: item.price || 0,
              amount: item.amount || 0,
              discount: item.discount || 0,
              description: item.description,
              createdate: item.createdate,
              hs_product_currency_code: item.hs_product_currency_code,
              hs_createdate: item.hs_createdate,
              hs_object_id: item.hs_object_id
            })),
            quotes: quotes
          };
          
          // Fix: Ensure customer exists
          if (!opportunityData.customer) {
            opportunityData.customer = {
              id: opportunityData.company_id || '',
              name: opportunityData.customer_name || ''
            };
          }
          
          // Fix: Derive customer_address if not provided but address components exist
          if (!opportunityData.customer.customer_address && opportunityData.customer.address) {
            const addressParts = [
              opportunityData.customer.address,
              opportunityData.customer.city,
              opportunityData.customer.state,
              opportunityData.customer.zip
            ].filter(Boolean);
            
            if (addressParts.length > 0) {
              opportunityData.customer.customer_address = addressParts.join(', ');
            }
          }
          
          setHubspotOpportunity(opportunityData);
          setIsHubspotOpportunity(true);
        } else {
          // Fetch regular opportunity
          const regularOpportunity = await getBusinessOpportunity(id);
          setOpportunity(regularOpportunity);
          setIsHubspotOpportunity(false);
        }
      } catch (error) {
        console.error('Error fetching opportunity data:', error);
        setErrorDetails({
          message: 'Erro ao carregar os dados da oportunidade',
          technical: error.message
        });
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOpportunityData();
  }, [id]);

  const handleBackClick = () => {
    navigate('/business-opportunities');
  };

  const handleQuoteClick = (quote: any) => {
    // Sempre navegar para a página interna de detalhes da proposta
    navigate(`/proposals/${quote.id}`);
  };

  const formatAddress = (customer: any) => {
    if (!customer) return 'Endereço não disponível';
    
    const addressParts = [
      customer.address,
      customer.city,
      customer.state,
      customer.zip
    ].filter(Boolean);
    
    return addressParts.length > 0 ? addressParts.join(', ') : 'Endereço não disponível';
  };

  // Add a function to handle image loading errors
  const handleImageError = (id: string) => {
    setFailedImages(prev => ({
      ...prev,
      [id]: true
    }));
  };

  // Add a function to get initials for fallback avatar
  const getInitials = (name: string): string => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Show error if something went wrong
  if (isError) {
    return <ErrorDisplay />;
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className="px-6 py-8 mx-auto max-w-[1920px] w-full">
        <div className="flex justify-center items-center flex-col h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          <p className="text-lg text-gray-500 mt-4">Carregando detalhes da oportunidade...</p>
        </div>
      </div>
    );
  }
  
  // If we have no data after loading is complete
  if (!opportunity && !hubspotOpportunity) {
    return (
      <div className="px-6 py-8 mx-auto max-w-[1920px] w-full">
        <div className="text-center">
          <div className="h-16 w-16 text-yellow-500 mx-auto mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
          </div>
          <h2 className="text-2xl font-extrabold">Oportunidade não encontrada</h2>
          <p className="text-gray-500 mt-2">
            Não foi possível encontrar uma oportunidade com o ID fornecido.
          </p>
          <Button onClick={() => navigate('/business-opportunities')} className="mt-6" variant="outline">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar para lista de oportunidades
          </Button>
        </div>
      </div>
    );
  }

  // Render the opportunity details - prioritize Hubspot data if available
  return (
    <div className="min-h-screen bg-[#FCFCFD]">
      {/* Hero Section with Fixed Background Color */}
      <div className="relative overflow-hidden" style={{ backgroundColor: '#1F1746' }}>
        <div className="absolute inset-0 bg-black/20" />
        
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full transform translate-x-48 -translate-y-48" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full transform -translate-x-32 translate-y-32" />
        
        <div className="relative z-10 container mx-auto px-6 py-16">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 text-white">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-4">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => navigate('/business-opportunities')}
                  className="text-white hover:bg-white/20 border-white/30"
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <div className="h-8 w-px bg-white/30" />
                <div className="flex items-center gap-2">
                  <BarChart4 className="h-6 w-6" />
                  <span className="text-white/80">Oportunidade</span>
                </div>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-extrabold mb-3">
                {isHubspotOpportunity && hubspotOpportunity
                  ? hubspotOpportunity.dealname
                  : opportunity?.name || 'Oportunidade'}
              </h1>
              
              <div className="flex flex-wrap items-center gap-4 text-white/90">
                <div className="flex items-center gap-2">
                  <Building className="h-4 w-4" />
                  <span>
                    {isHubspotOpportunity && hubspotOpportunity?.customer?.name 
                      ? hubspotOpportunity.customer.name 
                      : opportunity?.customer?.name || 'Cliente não identificado'}
                  </span>
                </div>
                <div className="h-4 w-px bg-white/30" />
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  <span className="font-bold">
                    {isHubspotOpportunity && hubspotOpportunity
                      ? formatCurrency(hubspotOpportunity.amount || 0, hubspotOpportunity.deal_currency_code || 'BRL')
                      : opportunity
                        ? formatCurrency(opportunity.total_value || 0, opportunity.currency_code || 'BRL')
                        : 'R$ 0,00'}
                  </span>
                </div>
                <div className="h-4 w-px bg-white/30" />
                <div className="flex items-center gap-2">
                  <Percent className="h-4 w-4" />
                  <span className="font-bold">
                    {isHubspotOpportunity && hubspotOpportunity && typeof hubspotOpportunity?.hs_deal_stage_probability === 'number'
                      ? (hubspotOpportunity.hs_deal_stage_probability <= 1 
                        ? Math.round(hubspotOpportunity.hs_deal_stage_probability * 100)
                        : Math.round(hubspotOpportunity.hs_deal_stage_probability))
                      : opportunity && typeof opportunity.probability === 'number'
                        ? Math.round(opportunity.probability * 100)
                        : 0
                    }% prob.
                  </span>
                </div>
                <div className="h-4 w-px bg-white/30" />
                <Badge 
                  className={`${
                    isHubspotOpportunity && hubspotOpportunity
                      ? (hubspotOpportunity?.label 
                          ? statusColors[mapHubspotStageToStatus(hubspotOpportunity.label)]
                          : hubspotOpportunity?.dealstage 
                            ? statusColors[mapHubspotStageToStatus(hubspotOpportunity.dealstage)]
                            : 'bg-blue-50 text-blue-700 border-blue-200')
                      : (opportunity?.status ? statusColors[opportunity.status] : 'bg-gray-50 text-gray-700 border-gray-200')
                  } bg-white/20 text-white border-white/30`}
                >
                  {
                    isHubspotOpportunity && hubspotOpportunity
                      ? (hubspotOpportunity?.label || (hubspotOpportunity?.dealstage ? getHubspotStageLabel(hubspotOpportunity.dealstage) : 'Não definido'))
                      : (opportunity?.status ? statusLabels[opportunity.status as keyof typeof statusLabels] : 'Desconhecido')
                  }
                </Badge>
              </div>
            </div>
            
            <div className="flex gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                  <Button 
                    variant="secondary"
                    className="bg-white/20 hover:bg-white/30 text-white border-white/30"
                  >
                  <MoreHorizontal className="h-4 w-4 mr-2" />
                    Ações
                </Button>
              </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => window.open(
                    isHubspotOpportunity && hubspotOpportunity?.hs_object_id 
                    ? `https://app.hubspot.com/contacts/9301423/deal/${hubspotOpportunity.hs_object_id}`
                    : '', '_blank')}>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Ver no HubSpot
                </DropdownMenuItem>
                {isHubspotOpportunity && hubspotOpportunity?.customer?.id && (
                  <DropdownMenuItem onClick={() => navigate(`/customers/${hubspotOpportunity.customer.id}`)}>
                    <Users className="h-4 w-4 mr-2" />
                    Ver Detalhes do Cliente
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
              
              <Button 
                onClick={() => navigate('/business-opportunities')}
                className="bg-white/20 hover:bg-white/30 text-white border-white/30"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Ver Todas
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 -mt-4 relative z-10 space-y-8">
      
              {/* Financial Metrics */}
        {isHubspotOpportunity && hubspotOpportunity && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
            {/* One-Time */}
            <Card className="relative overflow-hidden border-0 shadow-lg bg-gradient-to-br from-blue-50 to-white">
              <CardContent className="p-4">
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-blue-100 rounded-md">
                      <DollarSign className="h-4 w-4 text-blue-600" />
                    </div>
                    <span className="text-sm font-extrabold text-gray-600">One-Time</span>
                  </div>
                  <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs">Único</span>
                </div>
                <div className="text-lg font-extrabold text-gray-900 mb-1">
                  {formatCurrency(hubspotOpportunity.hs_onetime || 0, hubspotOpportunity.deal_currency_code || 'BRL')}
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-500">
                    One-Time Payments
                  </div>
                  {(hubspotOpportunity.hs_margin_onetime !== undefined && hubspotOpportunity.hs_margin_onetime !== null) && (
                    <div className="flex items-center gap-1">
                      <span className={`text-xs font-extrabold ${Number(hubspotOpportunity.hs_margin_onetime) >= 40 ? 'text-green-600' : Number(hubspotOpportunity.hs_margin_onetime) >= 20 ? 'text-amber-600' : 'text-red-600'}`}>
                        {hubspotOpportunity.hs_margin_onetime}%
                      </span>
                      <span className="text-xs text-gray-500">margem</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* MRR */}
            <Card className="relative overflow-hidden border-0 shadow-lg bg-gradient-to-br from-green-50 to-white">
              <CardContent className="p-4">
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-green-100 rounded-md">
                      <CalendarRange className="h-4 w-4 text-green-600" />
                    </div>
                    <span className="text-sm font-extrabold text-gray-600">MRR</span>
                  </div>
                  <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">Mensal</span>
                </div>
                <div className="text-lg font-extrabold text-gray-900 mb-1">
                  {formatCurrency(hubspotOpportunity.hs_mrr || 0, hubspotOpportunity.deal_currency_code || 'BRL')}
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-500">
                    Monthly Recurring Revenue
                  </div>
                  {(hubspotOpportunity.hs_margin_mrr !== undefined && hubspotOpportunity.hs_margin_mrr !== null) && (
                    <div className="flex items-center gap-1">
                      <span className={`text-xs font-extrabold ${Number(hubspotOpportunity.hs_margin_mrr) >= 40 ? 'text-green-600' : Number(hubspotOpportunity.hs_margin_mrr) >= 20 ? 'text-amber-600' : 'text-red-600'}`}>
                        {hubspotOpportunity.hs_margin_mrr}%
                      </span>
                      <span className="text-xs text-gray-500">margem</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* ARR */}
            <Card className="relative overflow-hidden border-0 shadow-lg bg-gradient-to-br from-emerald-50 to-white">
              <CardContent className="p-4">
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-emerald-100 rounded-md">
                      <Calendar className="h-4 w-4 text-emerald-600" />
                    </div>
                    <span className="text-sm font-extrabold text-gray-600">ARR</span>
                  </div>
                  <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs">Anual</span>
                </div>
                <div className="text-lg font-extrabold text-gray-900 mb-1">
                  {formatCurrency(hubspotOpportunity.hs_arr || 0, hubspotOpportunity.deal_currency_code || 'BRL')}
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-500">
                    Annual Recurring Revenue
                  </div>
                  {(hubspotOpportunity.hs_margin_arr !== undefined && hubspotOpportunity.hs_margin_arr !== null) && (
                    <div className="flex items-center gap-1">
                      <span className={`text-xs font-extrabold ${Number(hubspotOpportunity.hs_margin_arr) >= 40 ? 'text-green-600' : Number(hubspotOpportunity.hs_margin_arr) >= 20 ? 'text-amber-600' : 'text-red-600'}`}>
                        {hubspotOpportunity.hs_margin_arr}%
                      </span>
                      <span className="text-xs text-gray-500">margem</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* ACV */}
            <Card className="relative overflow-hidden border-0 shadow-lg bg-gradient-to-br from-purple-50 to-white">
              <CardContent className="p-4">
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-purple-100 rounded-md">
                      <BarChart4 className="h-4 w-4 text-purple-600" />
                    </div>
                    <span className="text-sm font-extrabold text-gray-600">ACV</span>
                  </div>
                  <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs">Primeiro Ano</span>
                </div>
                <div className="text-lg font-extrabold text-gray-900 mb-1">
                  {formatCurrency(hubspotOpportunity.hs_acv || 0, hubspotOpportunity.deal_currency_code || 'BRL')}
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-500">
                    Annual Contract Value
                  </div>
                  {(hubspotOpportunity.hs_margin_acv !== undefined && hubspotOpportunity.hs_margin_acv !== null) && (
                    <div className="flex items-center gap-1">
                      <span className={`text-xs font-extrabold ${Number(hubspotOpportunity.hs_margin_acv) >= 40 ? 'text-green-600' : Number(hubspotOpportunity.hs_margin_acv) >= 20 ? 'text-amber-600' : 'text-red-600'}`}>
                        {hubspotOpportunity.hs_margin_acv}%
                      </span>
                      <span className="text-xs text-gray-500">margem</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* TCV */}
            <Card className="relative overflow-hidden border-0 shadow-lg bg-gradient-to-br from-yellow-50 to-white">
              <CardContent className="p-4">
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-yellow-100 rounded-md">
                      <TrendingUp className="h-4 w-4 text-yellow-600" />
                    </div>
                    <span className="text-sm font-extrabold text-gray-600">TCV</span>
                  </div>
                  <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs">Duração Total</span>
                </div>
                <div className="text-lg font-extrabold text-gray-900 mb-1">
                  {formatCurrency(hubspotOpportunity.hs_tcv || 0, hubspotOpportunity.deal_currency_code || 'BRL')}
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-500">
                    Total Contract Value
                  </div>
                  {(hubspotOpportunity.hs_margin_tcv !== undefined && hubspotOpportunity.hs_margin_tcv !== null) && (
                    <div className="flex items-center gap-1">
                      <span className={`text-xs font-extrabold ${Number(hubspotOpportunity.hs_margin_tcv) >= 40 ? 'text-green-600' : Number(hubspotOpportunity.hs_margin_tcv) >= 20 ? 'text-amber-600' : 'text-red-600'}`}>
                        {hubspotOpportunity.hs_margin_tcv}%
                      </span>
                      <span className="text-xs text-gray-500">margem</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

      {/* Line Items Section */}
      {isHubspotOpportunity && hubspotOpportunity?.line_items && (
        <Card className="mb-8">
          <CardHeader className="pb-3">
            <div className="flex justify-between items-center">
              <CardTitle className="text-xl flex items-center">
                <Package className="mr-2 h-5 w-5 text-muted-foreground" />
                Itens da Oportunidade
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {hubspotOpportunity.line_items.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Quantidade</TableHead>
                    <TableHead>Preço Unitário</TableHead>
                    <TableHead>Desconto</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {hubspotOpportunity.line_items.map((item: HubspotLineItem) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-extrabold">
                        <div>
                          {item.name}
                          {item.description && (
                            <p className="text-xs text-muted-foreground mt-1">{item.description}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>
                        {formatCurrency(item.price, hubspotOpportunity.deal_currency_code || 'BRL')}
                      </TableCell>
                      <TableCell>
                        {item.discount > 0 ? `${item.discount}%` : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(item.amount, hubspotOpportunity.deal_currency_code || 'BRL')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8">
                <Package className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-muted-foreground">
                  Nenhum item encontrado para esta oportunidade
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Quotes Section */}
      {isHubspotOpportunity && hubspotOpportunity && (
        <Card className="mb-8">
          <CardHeader className="pb-3">
            <div className="flex justify-between items-center">
              <CardTitle className="text-xl flex items-center">
                <FileText className="mr-2 h-5 w-5 text-muted-foreground" />
                Cotações
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {hubspotOpportunity.quotes && hubspotOpportunity.quotes.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Número</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Valor</TableHead>
                    <TableHead>Data de Criação</TableHead>
                    <TableHead>Válido até</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {hubspotOpportunity.quotes.map((quote: any) => (
                    <TableRow 
                      key={quote.id} 
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => handleQuoteClick(quote)}
                    >
                      <TableCell className="font-extrabold">{quote.hs_quote_number || quote.id}</TableCell>
                      <TableCell>
                        <Badge variant={
                          quote.hs_status === 'APPROVED' ? 'success' :
                          quote.hs_status === 'PENDING' ? 'outline' :
                          quote.hs_status === 'REJECTED' ? 'destructive' : 'secondary'
                        }>
                          {quote.hs_status === 'APPROVED' ? 'Aprovado' :
                           quote.hs_status === 'PENDING' ? 'Pendente' :
                           quote.hs_status === 'REJECTED' ? 'Rejeitado' : quote.hs_status || 'Desconhecido'}
                        </Badge>
                      </TableCell>
                      <TableCell>{quote.hs_quote_amount ? formatCurrency(quote.hs_quote_amount, quote.hs_currency || 'BRL') : 'N/A'}</TableCell>
                      <TableCell>{quote.hs_createdate ? new Date(quote.hs_createdate).toLocaleDateString() : 'N/A'}</TableCell>
                      <TableCell>{quote.hs_expiration_date ? new Date(quote.hs_expiration_date).toLocaleDateString() : 'N/A'}</TableCell>
                      <TableCell className="text-right">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/proposals/${quote.id}`);
                          }}
                        >
                          Ver Detalhes
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8">
                <FileText className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-muted-foreground">
                  Nenhuma cotação encontrada para esta oportunidade
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      </div>

      {/* Bottom Spacing */}
      <div className="h-16" />
    </div>
  );
};

export default BusinessOpportunityDetail; 